
import { Component, Prop, Mixins, Provide } from "vue-property-decorator";
import RequestPromises from "@/mixins/requestPromises";
import { DataModules } from "@/store/modules/data";
import { mapState } from "vuex";
import { IState } from "@/store";
import { ITaxTemplate, ITaxTag } from "@/models/tax";
import { TranslationsTypes } from "@/data/enums/translations";
import {
  ITaxTemplateProvider,
  ITaxTemplateProviderMethods
} from "@/models/providers/tax";
import { IProvider } from "@/models/providers";
import { CreateElement } from "vue";

@Component({
  computed: {
    ...mapState({
      taxTemplate(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: this.dataModule
        });
      }
    })
  }
})
export default class TaxTemplateProvider extends Mixins(RequestPromises)
  implements IProvider {
  // Props
  @Prop({ default: undefined })
  public readonly taxTemplateId!: ITaxTemplate["id"];

  @Prop({ default: false })
  public handleLoading!: boolean;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({
    default: () => null
  })
  public onNotFound!: IProvider["onNotFound"];

  // Data
  public dataModule = DataModules.TAX_TEMPLATES;
  public taxTemplate!: ITaxTemplate;
  public isLoading = true;
  public hasRequestError = false;
  public isReloading = false;
  public isProcessing = false;
  public localTaxTemplateId = this.taxTemplateId;

  // Lifecycle methods
  public async created() {
    if (!this.contentAvailable) return;
    await this.loadData();
    this.$watch("taxTemplateId", val => (this.localTaxTemplateId = val));
  }

  public beforeDestroy() {
    this.$store.dispatch(`data/binSingle`, {
      storeModule: this.dataModule,
      vm: this
    });
  }

  // Getters

  public get isNewTemplate() {
    return !this.taxTemplate;
  }

  public get isFormDisabled() {
    return this.isProcessing || !this.canEdit || this.isSystemTemplate;
  }

  public get contentAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public get canEdit() {
    return this.$userCan("update_taxtemplate");
  }

  public get guardFunctionalities() {
    return ["get_taxtemplate", "list_taxbusinesstypes"];
  }

  public get isSystemTemplate() {
    return !!this.$_.get(this.taxTemplate, "system_template", false);
  }

  // Methods
  public async loadData(payload = {}) {
    try {
      if (this.localTaxTemplateId) await this.fetchTaxTemplate(payload);
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
      if (!this.$store.getters["api/is404"](error)) return;
      this.onNotFound && this.onNotFound(this);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public async fetchTaxTemplate(payload = {}) {
    if (!this.localTaxTemplateId) return Promise.reject();
    await this.$store.dispatch(`data/${this.dataModule}/get`, {
      id: this.localTaxTemplateId,
      vm: this,
      params: {
        with: "tax_tags.locations,business_types"
      },
      ...payload
    });
  }

  public async reloadData() {
    this.isReloading = true;
    this.fetchTaxTemplate({ ignoreStored: true });
    this.isReloading = false;
  }

  public async openTranslateModal(fieldName: string) {
    this.$store.dispatch(
      `data/${DataModules.TRANSLATIONS}/openTranslationsModal`,
      {
        props: {
          objectId: this.localTaxTemplateId,
          objectTypeCode: TranslationsTypes.TAX_TEMPLATE,
          brandId: this.taxTemplate.brand_id,
          fieldType: "text",
          fieldLabel: this.$t("_.name"),
          fieldName
        }
      }
    );
  }

  public createTaxTemplate(data: Partial<ITaxTemplate>) {
    return this.$store
      .dispatch(`data/${this.dataModule}/create`, {
        data
      })
      .then((taxTemplate: ITaxTemplate) => {
        this.localTaxTemplateId = taxTemplate.id;
        return taxTemplate;
      });
  }

  public updateTaxTemplate(
    id: ITaxTemplate["id"],
    data: Partial<ITaxTemplate>
  ) {
    return this.$store
      .dispatch(`data/${this.dataModule}/update`, {
        id,
        data
      })
      .then(template => {
        this.$buefy.toast.open({
          message: this.$t("_.object_successfully_updated", {
            object: this.$t("_.tax_template")
          }) as string
        });
        return template;
      });
  }

  public async syncTags(taxTagIds: ITaxTag["id"][]) {
    try {
      this.isReloading = true;
      await this.$store.dispatch(`data/${DataModules.TAX_TEMPLATES}/sync`, {
        taxTemplateId: this.localTaxTemplateId,
        data: {
          tax_template_id: this.localTaxTemplateId,
          tax_tag_ids: taxTagIds
        }
      });
    } finally {
      this.reloadData();
    }
  }

  @Provide("taxTemplateProvider")
  public providerData(): ITaxTemplateProvider {
    return {
      taxTemplateId: this.localTaxTemplateId,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      hasRequestError: this.hasRequestError,
      isProcessing: this.isProcessing,
      isNewTemplate: this.isNewTemplate,
      canEdit: this.canEdit,
      taxTemplate: this.taxTemplate,
      guardFunctionalities: this.guardFunctionalities,
      isSystemTemplate: this.isSystemTemplate
    };
  }

  @Provide("taxTemplateProviderMethods")
  public get providerMethods(): ITaxTemplateProviderMethods {
    return {
      reloadData: this.reloadData,
      openTranslateModal: this.openTranslateModal,
      createTaxTemplate: this.createTaxTemplate,
      updateTaxTemplate: this.updateTaxTemplate,
      syncTags: this.syncTags,
      set: (field: string, value: any) => this.$set(this, field, value)
    };
  }

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $taxTemplateData: this.providerData(),
        $taxTemplateMethods: this.providerMethods
      })
    );
  }
}
